// src/services/salonService.js
// Service pour gérer les salons - VERSION PRODUCTION

import api from './api';

/**
 * 🛠️ HELPER - Normaliser les URLs d'images
 * Gère 2 cas :
 * 1. URL déjà complète en BDD → retourne telle quelle
 * 2. Chemin relatif en BDD → construit l'URL complète
 *
 * Attention : ne pas faire replace('/api', '') — ça casse « api.shynea.one »
 * (le premier « /api » est dans le sous-domaine).
 */
const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl) return null;

  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.shynea.one/api';
  const baseUrl = apiUrl.replace(/\/api\/?$/, '');

  if (imageUrl.startsWith('/')) {
    return `${baseUrl}${imageUrl}`;
  }
  return `${baseUrl}/${imageUrl}`;
};

/**
 * 🛠️ HELPER - Transformer les données d'un salon
 * Normalise les URLs d'images (cover_image, image_url)
 */
const processSalon = (salon) => {
  if (!salon) return null;
  
  return {
    ...salon,
    cover_image: normalizeImageUrl(salon.cover_image || salon.image_url),
    image_url: normalizeImageUrl(salon.image_url || salon.cover_image)
  };
};

/**
 * 📡 SERVICE PRINCIPAL
 */
const salonService = {
  
  /**
   * Récupérer tous les salons actifs
   * @param {Object} filters - Filtres optionnels (city, limit)
   * @returns {Promise<Array>} Liste des salons
   */
  async getAllSalons(filters = {}) {
    try {
      const params = {};
      
      if (filters.city) params.city = filters.city;
      if (filters.limit) params.limit = filters.limit;

      const response = await api.get('/public/salons', { params });
      
      // Extraire les salons et normaliser les images
      const salons = (response.data.salons || response.data || []).map(processSalon);
      if (filters.withMeta) {
        return {
          salons,
          total: response.data.total ?? response.data.count ?? salons.length,
        };
      }
      return salons;
    } catch (error) {
      console.error('❌ Erreur getAllSalons:', error);
      throw error;
    }
  },

  /**
   * Recherche avancée de salons
   * @param {Object} filters - Filtres de recherche
   * @returns {Promise<Object>} Résultats de recherche
   */
  async searchSalons(filters = {}) {
    try {
      const params = {
        city: filters.city || undefined,
        service: filters.searchTerm || undefined,
        minRating: filters.minRating || undefined,
        sortBy: filters.sortBy || 'rating',
        sortOrder: filters.sortOrder || 'desc',
        limit: filters.limit || 20,
        offset: filters.offset || 0,
      };

      // Nettoyer les undefined
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) delete params[key];
      });

      const response = await api.get('/public/search', { params });
      
      const salons = response.data.salons || [];
      
      return {
        salons: salons.map(processSalon),
        total: response.data.total || 0,
        hasMore: response.data.hasMore || false
      };
    } catch (error) {
      console.error('❌ Erreur searchSalons:', error);
      throw error;
    }
  },

  /**
   * Récupérer un salon par ID
   * @param {string} salonId - ID du salon
   * @returns {Promise<Object>} Détails du salon
   */
  async getSalonById(salonId) {
    try {
      const response = await api.get(`/public/salons/${salonId}`);
      
      const salon = response.data.salon || response.data;
      
      return processSalon(salon);
    } catch (error) {
      console.error(`❌ Erreur getSalonById(${salonId}):`, error);
      throw error;
    }
  },

  /**
   * Récupérer les services d'un salon
   * @param {string} salonId - ID du salon
   * @returns {Promise<Array>} Liste des services
   */
  async getSalonServices(salonId) {
    try {
      const response = await api.get(`/public/salons/${salonId}/services`);
      
      return response.data.services || [];
    } catch (error) {
      console.error(`❌ Erreur getSalonServices(${salonId}):`, error);
      throw error;
    }
  },

  /**
   * Récupérer les statistiques de recherche
   * @returns {Promise<Object>} Statistiques
   */
  async getSearchStats() {
    try {
      const response = await api.get('/public/search-stats');
      
      return response.data.stats || {};
    } catch (error) {
      console.error('❌ Erreur getSearchStats:', error);
      throw error;
    }
  },
};

export { normalizeImageUrl };
export default salonService;